import React from 'react';

import { type UUID, isDefined } from '@nfctron/nexus-core';
import { type ExtendedComponent, UiButton, useFormat } from '@nfctron/nexus-react';

import { ShoppingCartIcon } from 'lucide-react';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';

import { CartStoreExtraProductsSlice, useCartStore, useCartStoreExtraProductsProvider } from '../../stores/cartStore';

import { ProductCardBase } from './ProductCardBase';

/**
 * Event/Cart/ProductCardProduct component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const ProductCardProduct: React.FunctionComponent<ProductCardProductProps> = ({ ticketProductId, cartTicketId, ...props }) => {
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, ProductCardProduct.displayName);
	const format = useFormat();

	const cartConfig = useCartStore((state) => state.cartConfig);
	const { ticket } = useCartStore((state) => state.getCartedTicket({ cartTicketId }));

	const {
		productGetters,
		cartOps,
		sheetConfig: { productInput, setProductInput },
	} = useCartStoreExtraProductsProvider();

	const extraProductConfig = productGetters.getByIdWithLimit({ ticketProductId, ticketTypeId: ticket.ticketTypeId });
	const isConfigurationRequired = productGetters.isConfigRequired({ ticketProductId, ticketTypeId: ticket.ticketTypeId });
	const productIs = productGetters.getState({ ticketProductId, cartTicketId });

	return (
		<ProductCardBase
			title={extraProductConfig.name}
			description={extraProductConfig.description}
			gallery={extraProductConfig.gallery}
			type={CartStoreExtraProductsSlice.PRODUCT_CONTROL_TYPE.SIMPLE}
			price={format.price(extraProductConfig.price, cartConfig.currencyId)}
			actions={
				<React.Fragment>
					{/* cart button */}
					<UiButton
						variant="primary"
						size="sm"
						leftSection={<ShoppingCartIcon />}
						loading={isDefined(productInput)}
						disabled={productIs.quantityLimited || productIs.soldOut}
						onClick={() => {
							if (isConfigurationRequired) {
								/** configuration needed, open config modal and continue there */
								setProductInput({
									ticketProductId,
									cartTicketId,
								});
							} else {
								/** no configuration needed, auto add to cart */
								const alreadyCarted = ticket.extraProducts.find((ep) => ep.ticketProductId === ticketProductId);
								/** if already carted, update current product instance */
								if (isDefined(alreadyCarted)) {
									cartOps.update({
										cartProductId: alreadyCarted.cartProductId,
										cartTicketId,
										update: { count: alreadyCarted.count + 1 },
									});
								} else {
									cartOps.add({
										ticketProductId,
										cartTicketId,
										initialValue: {},
									});
								}
							}
						}}
					>
						{t('addToCartButton.text')}
					</UiButton>
				</React.Fragment>
			}
			{...props}
		/>
	);
};

/**
 * Event/Cart/ProductCardProduct component display name
 * @type {string}
 */
ProductCardProduct.displayName = 'Event/Cart/ProductCardProduct';

/**
 * Event/Cart/ProductCardProduct component props type
 * @export
 */
export type ProductCardProductProps = ExtendedComponent<
	{
		cartTicketId: UUID;
		ticketProductId: UUID;
	},
	typeof ProductCardBase
>;
